import React, { useCallback, useEffect, useState } from "react";
import { Bookmark, BookmarkPlus, Trash2, Share2, Check, X } from "lucide-react";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/auth.jsx";

/**
 * SavedViewsBar — Chips that allow the user to switch between saved column views
 * for a given list (view_key). Saved views capture: visible columns, sort, widths.
 *
 * Props:
 *  - viewKey: string (e.g. "service_cases", "report:open-cases", "dashboard_drilldown")
 *  - config: the object returned by useColumnConfig(viewKey)
 */
export default function SavedViewsBar({ viewKey, config }) {
  const { user } = useAuth();
  const currentUserId = user?.id;
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [name, setName] = useState("");
  const [isShared, setIsShared] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/users/me/saved-views`, { params: { view_key: viewKey } });
      setViews(data);
    } catch {
      setViews([]);
    } finally {
      setLoading(false);
    }
  }, [viewKey]);

  useEffect(() => { load(); }, [load]);

  const applyView = (v) => {
    config.setVisible(v.visible);
    if (v.sort_key) config.setSort(v.sort_key, v.sort_dir || "asc");
    setActiveId(v.id);
  };

  const saveCurrent = async () => {
    if (!name.trim()) return;
    try {
      const { data } = await api.post(`/users/me/saved-views`, {
        view_key: viewKey,
        name: name.trim(),
        visible: config.visible,
        sort_key: config.sortKey,
        sort_dir: config.sortDir,
        widths: config.widths,
        is_shared: isShared,
      });
      setViews([data, ...views]);
      setActiveId(data.id);
      setName("");
      setIsShared(false);
      setShowSave(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Falha ao guardar a vista.";
      alert(msg);
    }
  };

  const removeView = async (v) => {
    if (!confirm(`Apagar a vista "${v.name}"?`)) return;
    try {
      await api.delete(`/users/me/saved-views/${v.id}`);
      setViews(views.filter((x) => x.id !== v.id));
      if (activeId === v.id) setActiveId(null);
    } catch (e) {
      alert(e?.response?.data?.detail || "Falha ao apagar.");
    }
  };

  const toggleShare = async (v) => {
    try {
      const { data } = await api.patch(`/users/me/saved-views/${v.id}`, { is_shared: !v.is_shared });
      setViews(views.map((x) => (x.id === v.id ? data : x)));
    } catch (e) {
      alert(e?.response?.data?.detail || "Falha ao actualizar.");
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`saved-views-bar-${viewKey}`}>
      <div className="text-[10px] uppercase tracking-widest text-[#6C757D] mr-1 flex items-center gap-1">
        <Bookmark className="w-3 h-3" /> Vistas guardadas:
      </div>

      {loading ? (
        <span className="text-xs text-[#6C757D]">A carregar…</span>
      ) : views.length === 0 ? (
        <span className="text-xs text-[#6C757D] italic">Nenhuma ainda</span>
      ) : (
        views.map((v) => (
          <SavedViewChip
            key={v.id}
            v={v}
            active={activeId === v.id}
            isOwner={v.owner_user_id === currentUserId}
            onApply={() => applyView(v)}
            onRemove={() => removeView(v)}
            onToggleShare={() => toggleShare(v)}
          />
        ))
      )}

      {showSave ? (
        <div className="inline-flex items-center gap-1 bg-white border border-[#1F3864] rounded-sm px-2 py-1" data-testid={`saved-views-save-form-${viewKey}`}>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") saveCurrent(); if (e.key === "Escape") { setShowSave(false); setName(""); } }}
            placeholder="Nome da vista"
            className="text-xs outline-none w-32"
            data-testid="saved-views-name-input"
          />
          <label className="inline-flex items-center gap-1 text-[10px] text-[#6C757D] cursor-pointer">
            <input
              type="checkbox"
              checked={isShared}
              onChange={(e) => setIsShared(e.target.checked)}
              data-testid="saved-views-share-checkbox"
            />
            Partilhar
          </label>
          <button onClick={saveCurrent} className="text-[#1F3864] hover:text-[#142542]" data-testid="saved-views-save-confirm">
            <Check className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => { setShowSave(false); setName(""); }} className="text-[#6C757D] hover:text-[#1A1A2E]">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <button
          onClick={() => setShowSave(true)}
          className="inline-flex items-center gap-1 text-xs text-[#1F3864] hover:bg-[#F4F6F8] border border-dashed border-gray-300 hover:border-[#1F3864] rounded-sm px-2 py-1"
          data-testid={`saved-views-save-btn-${viewKey}`}
        >
          <BookmarkPlus className="w-3 h-3" /> Guardar vista actual
        </button>
      )}
    </div>
  );
}

function SavedViewChip({ v, active, isOwner, onApply, onRemove, onToggleShare }) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs rounded-sm px-2 py-1 border ${
        active ? "bg-[#1F3864] text-white border-[#1F3864]" : "bg-white text-[#1A1A2E] border-gray-200 hover:border-[#1F3864]"
      }`}
      data-testid={`saved-view-chip-${v.id}`}
    >
      <button onClick={onApply} className="font-medium" data-testid={`saved-view-apply-${v.id}`}>
        {v.name}
      </button>
      {v.is_shared && !isOwner && (
        <span className={`text-[9px] uppercase tracking-widest ${active ? "bg-white/20 text-white" : "bg-[#E0E7FF] text-[#1E3A8A]"} px-1 rounded-sm`} title={`Partilhada por ${v.owner_name}`}>
          {v.owner_name ? `de ${v.owner_name.split(" ")[0]}` : "shared"}
        </span>
      )}
      {isOwner && (
        <>
          <button
            onClick={onToggleShare}
            className={`p-0.5 rounded ${active ? "hover:bg-white/10" : "hover:bg-gray-100"} ${v.is_shared ? "text-[#1F3864]" : ""}`}
            title={v.is_shared ? "Tornar privada" : "Partilhar"}
            data-testid={`saved-view-share-${v.id}`}
          >
            <Share2 className="w-3 h-3" />
          </button>
          <button
            onClick={onRemove}
            className={`p-0.5 rounded ${active ? "hover:bg-white/10" : "hover:bg-gray-100"}`}
            title="Apagar"
            data-testid={`saved-view-remove-${v.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </>
      )}
    </span>
  );
}
